import { useNavigate, useParams } from 'react-router-dom'

import { TimePlanningProfileIndexPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import PlanningVersions from 'modules/time_planning/pages/PlanningVersionsPage'

const IndexPage = () => {
  const { navigation } = useNavigation()

  const { id: employeeIdparam } = useStrictParams<'id'>()
  const { employee: sessionEmployee } = useCurrentSession()
  const employeeId = employeeIdparam || sessionEmployee.id

  const { planningVersionId: planningVersionIdparam } = useParams()
  const navigate = useNavigate()

  // Use '0' as default when planningVersionId is missing ('0' is the default planning version)
  const planningVersionId = planningVersionIdparam ?? '0'
  // Skip query if planningVersionId is invalid (NaN)
  const isValidPlanningVersionId = !!planningVersionId

  const { data, loading } = useQuery(TimePlanningProfileIndexPageDocument, {
    variables: {
      employeeId: employeeId,
      planningVersionId: planningVersionId,
    },
    skip: !isValidPlanningVersionId,
  })

  const currentPlanningVersionId = data?.timePlanning.planningVersionsConnection.nodes[0]?.id
  const canSeePlanningVersions =
    data?.employees.employee?._policies?.seePlanningVersions?.can ?? false
  const canEditPlanningVersions =
    data?.employees.employee?._policies?.editPlanningVersions?.can ?? false
  const canEditShifts = data?.shiftManagement._policies?.shiftCreateEverybody?.can ?? false
  const canEditContracts = data?.employees.employee?._policies?.editContract?.can ?? false

  useUnsafeEffect(() => {
    if (planningVersionId === '0' && currentPlanningVersionId !== undefined) {
      navigate(
        navigation.profile.planningVersions.planningVersionId.fullPath({
          planningVersionId: currentPlanningVersionId ?? '0', // 0 is the default planning version
        })
      )
    }
  }, [planningVersionId, currentPlanningVersionId, navigate, navigation])

  if (!loading && !canSeePlanningVersions) {
    navigate(navigation.profile.fullPath())
  }

  if (loading) return <Loading />

  return (
    <PlanningVersions
      employeeId={employeeId}
      canSeePlanningVersions={canSeePlanningVersions}
      canEditPlanningVersions={canEditPlanningVersions}
      canEditShifts={canEditShifts}
      canEditContracts={canEditContracts}
    />
  )
}

export default IndexPage
